<template>
  <div class="odoo-integration-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="page-header">
          <h1 class="page-title">Odoo CRM Integration</h1>
          <p class="page-subtitle">Connect and manage your Odoo CRM integration and sync settings</p>
        </div>

        <div class="odoo-content">
          <!-- Connection Status Card -->
          <div class="odoo-status-card">
            <div class="status-header">
              <div class="status-logo">
                <img src="https://upload.wikimedia.org/wikipedia/commons/4/4d/Odoo_logo_rgb.svg"
                     alt="Odoo CRM"
                     class="odoo-logo" />
              </div>
              <div class="status-info">
                <h3>Odoo CRM Integration</h3>
                <div class="status-badge" :class="statusColor">
                  <span class="status-icon">{{ statusIcon }}</span>
                  <span>{{ statusText }}</span>
                </div>
              </div>
              <div class="status-actions">
                <button v-if="!odooStatus.connected"
                        @click="showConnectModal = true"
                        :disabled="connecting"
                        class="btn btn-primary">
                  <span v-if="connecting" class="loading-spinner"></span>
                  {{ connecting ? 'Connecting...' : 'Connect Odoo CRM' }}
                </button>
                <button v-else
                        @click="showDisconnectModal = true"
                        class="btn btn-danger">
                  Disconnect
                </button>
              </div>
            </div>

            <div v-if="odooStatus.connected" class="status-details">
              <div class="detail-item">
                <span class="detail-label">Last Sync:</span>
                <span class="detail-value">{{ formatLastSync(odooStatus.last_sync_at) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Odoo URL:</span>
                <span class="detail-value">{{ odooStatus.odoo_url }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Database:</span>
                <span class="detail-value">{{ odooStatus.odoo_database }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Connection:</span>
                <span class="detail-value" :class="{ 'text-danger': !odooStatus.connection_valid }">
                  {{ odooStatus.connection_valid ? 'Valid' : 'Invalid' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Sync Settings -->
          <div v-if="odooStatus.connected" class="sync-settings-card">
            <div class="card-header">
              <h3>Sync Settings</h3>
              <p>Configure what data to automatically sync with Odoo CRM</p>
            </div>

            <div class="settings-grid">
              <div class="setting-item">
                <div class="setting-info">
                  <h4>Enable CRM Sync</h4>
                  <p>Master toggle for all CRM synchronization</p>
                </div>
                <label class="toggle-switch">
                  <input type="checkbox" v-model="syncSettings.sync_enabled" @change="updateSettings">
                  <span class="toggle-slider"></span>
                </label>
              </div>

              <div class="setting-item" :class="{ disabled: !syncSettings.sync_enabled }">
                <div class="setting-info">
                  <h4>Auto-sync Call Analysis</h4>
                  <p>Automatically create contacts and activities from AI call analysis</p>
                </div>
                <label class="toggle-switch">
                  <input type="checkbox"
                         v-model="syncSettings.auto_sync_calls"
                         :disabled="!syncSettings.sync_enabled"
                         @change="updateSettings">
                  <span class="toggle-slider"></span>
                </label>
              </div>

              <div class="setting-item" :class="{ disabled: !syncSettings.sync_enabled }">
                <div class="setting-info">
                  <h4>Auto-sync WhatsApp Leads</h4>
                  <p>Automatically create lead records from WhatsApp conversations</p>
                </div>
                <label class="toggle-switch">
                  <input type="checkbox"
                         v-model="syncSettings.auto_sync_whatsapp"
                         :disabled="!syncSettings.sync_enabled"
                         @change="updateSettings">
                  <span class="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          <!-- Sync History -->
          <div v-if="odooStatus.connected" class="sync-history-card">
            <div class="card-header">
              <h3>Sync History</h3>
              <div class="history-filters">
                <select v-model="historyFilter.sync_type" @change="loadSyncHistory" class="filter-select">
                  <option value="">All Types</option>
                  <option value="call_analysis">Call Analysis</option>
                  <option value="whatsapp_lead">WhatsApp Leads</option>
                  <option value="appointment">Appointment</option>
                  <option value="manual_sync">Manual Sync</option>
                </select>
                <select v-model="historyFilter.success" @change="loadSyncHistory" class="filter-select">
                  <option value="">All Status</option>
                  <option value="true">Success</option>
                  <option value="false">Failed</option>
                </select>
              </div>
            </div>

            <div class="sync-history-table">
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>CRM Record</th>
                    <th>Status</th>
                    <th>Response Time</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="log in syncHistory.logs" :key="log.id">
                    <td>
                      <span class="sync-type-badge" :class="log.sync_type">
                        {{ formatSyncType(log.sync_type) }}
                      </span>
                    </td>
                    <td>{{ log.crm_record_type }}</td>
                    <td>
                      <span class="status-badge" :class="log.success ? 'success' : 'error'">
                        {{ log.success ? 'Success' : 'Failed' }}
                      </span>
                      <div v-if="!log.success && log.error_message" class="error-message">
                        {{ log.error_message }}
                      </div>
                    </td>
                    <td>{{ log.response_time_ms }}ms</td>
                    <td>{{ formatDate(log.created_at) }}</td>
                  </tr>
                </tbody>
              </table>

              <div v-if="syncHistory.logs && syncHistory.logs.length === 0" class="empty-state">
                <p>No sync history found</p>
              </div>

              <!-- Pagination -->
              <div v-if="syncHistory.pagination && syncHistory.pagination.total_pages > 1" class="pagination">
                <button @click="changePage(syncHistory.pagination.current_page - 1)"
                        :disabled="!syncHistory.pagination.has_prev_page"
                        class="btn btn-secondary">
                  Previous
                </button>
                <span class="page-info">
                  Page {{ syncHistory.pagination.current_page }} of {{ syncHistory.pagination.total_pages }}
                </span>
                <button @click="changePage(syncHistory.pagination.current_page + 1)"
                        :disabled="!syncHistory.pagination.has_next_page"
                        class="btn btn-secondary">
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Connect Modal -->
    <div v-if="showConnectModal" class="modal-overlay" @click="showConnectModal = false">
      <div class="modal-content connect-modal" @click.stop>
        <div class="modal-header">
          <h3>Connect to Odoo CRM</h3>
        </div>
        <div class="modal-body">
          <form @submit.prevent="connectOdoo">
            <div class="form-group">
              <label for="odoo-url">Odoo URL</label>
              <input
                id="odoo-url"
                v-model="connectionForm.odoo_url"
                type="url"
                placeholder="https://your-company.odoo.com"
                required
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="database">Database Name</label>
              <input
                id="database"
                v-model="connectionForm.database"
                type="text"
                placeholder="your-database-name"
                required
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="username">Username</label>
              <input
                id="username"
                v-model="connectionForm.username"
                type="text"
                placeholder="admin"
                required
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="password">Password / API Key</label>
              <input
                id="password"
                v-model="connectionForm.password"
                type="password"
                placeholder="Your password or API key"
                required
                class="form-input"
              />
            </div>
            <div class="form-actions">
              <button type="button" @click="testConnection" :disabled="testing" class="btn btn-secondary">
                {{ testing ? 'Testing...' : 'Test Connection' }}
              </button>
              <div class="connection-result" v-if="testResult">
                <span :class="testResult.success ? 'success' : 'error'">
                  {{ testResult.message }}
                </span>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button @click="showConnectModal = false" class="btn btn-secondary">Cancel</button>
          <button @click="connectOdoo" :disabled="connecting || !isFormValid" class="btn btn-primary">
            {{ connecting ? 'Connecting...' : 'Connect' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Disconnect Confirmation Modal -->
    <div v-if="showDisconnectModal" class="modal-overlay" @click="showDisconnectModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Disconnect Odoo CRM</h3>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to disconnect Odoo CRM integration?</p>
          <p class="warning-text">This will stop all automatic syncing and remove access to your CRM data.</p>
        </div>
        <div class="modal-footer">
          <button @click="showDisconnectModal = false" class="btn btn-secondary">Cancel</button>
          <button @click="disconnectOdoo" :disabled="disconnecting" class="btn btn-danger">
            {{ disconnecting ? 'Disconnecting...' : 'Disconnect' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { OdooService } from '@/services/odooService'

export default {
  name: 'OdooIntegrationView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      odooStatus: {
        connected: false,
        connection_valid: false,
        sync_enabled: false,
        auto_sync_calls: false,
        auto_sync_whatsapp: false,
        last_sync_at: null,
        odoo_url: '',
        odoo_database: ''
      },
      syncSettings: {
        sync_enabled: false,
        auto_sync_calls: false,
        auto_sync_whatsapp: false
      },
      syncHistory: {
        logs: [],
        pagination: null
      },
      historyFilter: {
        sync_type: '',
        success: '',
        page: 1
      },
      connectionForm: {
        odoo_url: '',
        database: '',
        username: '',
        password: ''
      },
      connecting: false,
      disconnecting: false,
      testing: false,
      showConnectModal: false,
      showDisconnectModal: false,
      testResult: null,
      loading: true
    }
  },
  computed: {
    statusText () {
      if (this.odooStatus.connected && !this.odooStatus.connection_valid) return 'Connection Invalid'
      return this.odooStatus.connected ? 'Connected' : 'Not Connected'
    },
    statusColor () {
      if (this.odooStatus.connected && !this.odooStatus.connection_valid) return 'warning'
      return this.odooStatus.connected ? 'success' : 'error'
    },
    statusIcon () {
      if (this.odooStatus.connected && !this.odooStatus.connection_valid) return '⚠️'
      return this.odooStatus.connected ? '✓' : '○'
    },
    isFormValid () {
      return this.connectionForm.odoo_url &&
             this.connectionForm.database &&
             this.connectionForm.username &&
             this.connectionForm.password
    }
  },
  mounted () {
    this.loadOdooStatus()
  },
  methods: {
    async loadOdooStatus () {
      try {
        this.loading = true
        const status = await OdooService.getStatus()
        this.odooStatus = { ...this.odooStatus, ...status }

        if (status.connected) {
          this.syncSettings = {
            sync_enabled: status.sync_enabled,
            auto_sync_calls: status.auto_sync_calls,
            auto_sync_whatsapp: status.auto_sync_whatsapp
          }
          await this.loadSyncHistory()
        }
      } catch (error) {
        console.error('Error loading Odoo status:', error)
        this.$emit('show-toast', {
          type: 'error',
          message: 'Failed to load Odoo status'
        })
      } finally {
        this.loading = false
      }
    },

    async testConnection () {
      if (!this.isFormValid) return

      try {
        this.testing = true
        this.testResult = null

        await OdooService.testConnection(this.connectionForm)
        this.testResult = {
          success: true,
          message: 'Connection successful!'
        }
      } catch (error) {
        console.error('Error testing connection:', error)
        this.testResult = {
          success: false,
          message: error.message || 'Connection failed'
        }
      } finally {
        this.testing = false
      }
    },

    async connectOdoo () {
      if (!this.isFormValid) return

      try {
        this.connecting = true
        await OdooService.connect(this.connectionForm)

        this.showConnectModal = false
        this.connectionForm = {
          odoo_url: '',
          database: '',
          username: '',
          password: ''
        }
        this.testResult = null

        await this.loadOdooStatus()

        this.$emit('show-toast', {
          type: 'success',
          message: 'Odoo CRM connected successfully!'
        })
      } catch (error) {
        console.error('Error connecting Odoo:', error)
        this.$emit('show-toast', {
          type: 'error',
          message: error.message || 'Failed to connect to Odoo CRM'
        })
      } finally {
        this.connecting = false
      }
    },

    async disconnectOdoo () {
      try {
        this.disconnecting = true
        await OdooService.disconnect()

        this.odooStatus.connected = false
        this.showDisconnectModal = false

        this.$emit('show-toast', {
          type: 'success',
          message: 'Odoo CRM disconnected successfully'
        })
      } catch (error) {
        console.error('Error disconnecting Odoo:', error)
        this.$emit('show-toast', {
          type: 'error',
          message: error.message || 'Failed to disconnect Odoo CRM'
        })
      } finally {
        this.disconnecting = false
      }
    },

    async updateSettings () {
      try {
        await OdooService.updateSettings(this.syncSettings)
        this.$emit('show-toast', {
          type: 'success',
          message: 'Settings updated successfully'
        })
      } catch (error) {
        console.error('Error updating settings:', error)
        this.$emit('show-toast', {
          type: 'error',
          message: error.message || 'Failed to update settings'
        })
        // Reload status to revert changes
        await this.loadOdooStatus()
      }
    },

    async loadSyncHistory () {
      try {
        const params = {
          page: this.historyFilter.page,
          per_page: 20
        }

        if (this.historyFilter.sync_type) {
          params.sync_type = this.historyFilter.sync_type
        }

        if (this.historyFilter.success !== '') {
          params.success = this.historyFilter.success
        }

        const history = await OdooService.getSyncHistory(params)
        this.syncHistory = history
      } catch (error) {
        console.error('Error loading sync history:', error)
      }
    },

    async changePage (page) {
      this.historyFilter.page = page
      await this.loadSyncHistory()
    },

    formatLastSync (timestamp) {
      return OdooService.formatLastSync(timestamp)
    },

    formatSyncType (type) {
      const types = {
        call_analysis: 'Call Analysis',
        whatsapp_lead: 'WhatsApp Lead',
        appointment: 'Appointment',
        manual_sync: 'Manual Sync',
        customer_data: 'Customer Data'
      }
      return types[type] || type
    },

    formatDate (timestamp) {
      const date = new Date(timestamp)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    }
  }
}
</script>

<style scoped>
.odoo-integration-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-primary);
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 280px;
}

.page-content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.page-header {
  margin-bottom: 32px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
}

.odoo-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Status Card */
.odoo-status-card {
  background: var(--bg-card);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 24px;
  box-shadow: var(--shadow-sm);
}

.status-header {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 20px;
}

.status-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: white;
  border-radius: 12px;
  padding: 12px;
  border: 1px solid var(--border-secondary);
}

.odoo-logo {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.status-info {
  flex: 1;
}

.status-info h3 {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
}

.status-badge.success {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.status-badge.error {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.status-badge.warning {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.status-actions {
  display: flex;
  gap: 12px;
}

.status-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  padding-top: 20px;
  border-top: 1px solid var(--border-secondary);
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.detail-value.text-danger {
  color: #ef4444;
}

/* Settings Card */
.sync-settings-card,
.sync-history-card {
  background: var(--bg-card);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.card-header {
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-secondary);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.card-header h3 {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.card-header p {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
}

.history-filters {
  display: flex;
  gap: 12px;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 14px;
}

.settings-grid {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  background: var(--bg-secondary);
  border-radius: 8px;
  transition: opacity 0.2s;
}

.setting-item.disabled {
  opacity: 0.5;
}

.setting-info h4 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.setting-info p {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
}

/* Toggle Switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--border-primary);
  transition: 0.2s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.2s;
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background-color: #3b82f6;
}

input:checked + .toggle-slider:before {
  transform: translateX(24px);
}

input:disabled + .toggle-slider {
  opacity: 0.5;
  cursor: not-allowed;
}

/* History Table */
.sync-history-table {
  padding: 24px;
}

.sync-history-table table {
  width: 100%;
  border-collapse: collapse;
}

.sync-history-table th,
.sync-history-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid var(--border-secondary);
}

.sync-history-table th {
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sync-history-table td {
  color: var(--text-primary);
  font-size: 14px;
}

.sync-type-badge {
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.error-message {
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: var(--text-secondary);
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--border-secondary);
}

.page-info {
  font-size: 14px;
  color: var(--text-secondary);
}

/* Connect Modal */
.connect-modal {
  max-width: 500px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: var(--text-primary);
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.form-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}

.connection-result {
  font-size: 14px;
  font-weight: 500;
}

.connection-result .success {
  color: #10b981;
}

.connection-result .error {
  color: #ef4444;
}

/* Buttons */
.btn {
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--bg-tertiary);
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
}

/* Loading Spinner */
.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: var(--bg-card);
  border-radius: 12px;
  max-width: 400px;
  width: 90%;
  box-shadow: var(--shadow-lg);
}

.modal-header {
  padding: 20px 24px 0;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.modal-body {
  padding: 16px 24px;
}

.modal-body p {
  margin: 0 0 12px;
  color: var(--text-primary);
}

.warning-text {
  color: var(--text-secondary);
  font-size: 14px;
}

.modal-footer {
  padding: 16px 24px 24px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
  }

  .page-content {
    padding: 16px;
  }

  .status-header {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }

  .status-details {
    grid-template-columns: 1fr;
  }

  .card-header {
    flex-direction: column;
    gap: 12px;
  }

  .history-filters {
    width: 100%;
  }

  .filter-select {
    flex: 1;
  }

  .sync-history-table {
    overflow-x: auto;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
  }

  .connect-modal {
    max-width: 95%;
  }
}
</style>
